/**
 * @file: task.service.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { EnvTagType } from '@euler/config';
import { HttpClient } from '@euler/lib/request';
import {
  ConstructionWorkItemDetail,
  DeliveryCheckWorkItem,
  FinishTaskPreparedData,
  InspectedItemLabel,
  InspectionFlowSummary,
  UpdateTaskInformation,
  VehicleInspectionFlow,
  VehicleIssuesFilter,
  VehicleServiceRecordsFilter,
} from '@euler/model';
import { FinishTaskRequestPayload } from '@euler/model/FinishTaskRequestPayload';
import { PendingIssueInfoWithDetail } from '@euler/model/PendingIssueInfoWithDetail';
import { StoreWorkshopJob } from '@euler/model/StoreWorkshopJob';
import { StoreWorkshopStats } from '@euler/model/StoreWorkshopStats';
import { TaskSearchResultItem } from '@euler/model/TaskSearchResultItem';
import { IdType, Range } from '@euler/model/common';
import {
  VehicleInspectionTask,
  VehicleInspectionTaskEvent,
} from '@euler/model/entity';
import {
  CustomerType,
  DeliveryCheckSceneType,
  InspectionCategory,
  InspectionTaskStatus,
  SiteInspectionType,
  SubscribeBindActionType,
} from '@euler/model/enum';
import { ProductAuthorInfo } from '@euler/model/product';
import {
  MediaSourceType,
  VehicleReport,
  VehicleReportProjection,
} from '@euler/model/report';
import {
  Construction,
  ConstructionJob,
  ConstructionJobCommitPayload,
  ConstructionJobContextData,
  ConstructionJobMedia,
  ConstructionJobProductConfirmationItemPayload,
  ConstructionJobProductConfirmationResult,
  CustomIssue,
  CustomIssueCommitPayload,
  DeliveryCheck,
  DeliveryCheckCommitPayload,
  DeliveryCheckItem,
  FinishDeliveryChecksPayload,
  InspectionDetail,
  MaintenancePlan,
  PatchConstructionJobPayload,
  QuotationForm,
  QuotationFormPayload,
  SamplingCheck,
  SaveMaintenancePlanDetailPayload,
  SaveTaskProductManifestItemsPayload,
  ScheduleConstructionJobPayload,
  SettlementForm,
  SiteInspection,
  SiteInspectionCommitPayload,
  TaskBasicInfo,
  TaskDetail,
  TaskDetailProjection,
  TaskDetailVersionKey,
  TaskDetailVersions,
  TaskExtraInfo,
  TaskMediaWithSource,
  TaskProductManifest,
  TaskProductManifestItem,
  TaskProductManifestItemIdentifier,
  VersionedResponse,
} from '@euler/model/task-detail';
import {
  ObdTroubleCode,
  ObdTroubleCodeCommitPayload,
} from '@euler/model/task-detail/ObdTroubleCode';
import { ReportTabKey } from '@euler/model/viewmodel';
import { getCommonRequestParametersAsSearchQuery } from '@euler/services/utils';
import { formatDateTime } from '@euler/utils/formatters/datetime';

export type TaskListSearchParams = {
  startDate?: string | Date;
  endDate?: string | Date;
  licensePlateNo?: string;
  keyword?: string;
  status?: InspectionTaskStatus | InspectionTaskStatus[];
  childStatus?: 'inspected' | 'constructed';
  customerType?: CustomerType;
  customerIsOnSite?: boolean;
  carwash?: boolean;
  tag?: string;
  serviceAgentId?: number;
  brand?: string;
  fuelType?: string;
  mileageRange?: Range<number>;
  assignedTo?: number;
  parentTaskNo?: string;
};

export class TaskService {
  constructor(private readonly api: HttpClient) {}

  //#region basic

  async getExistingActiveTask(args: {
    vin?: string;
    licensePlateNo?: string;
  }): Promise<VehicleInspectionTask | null> {
    return await this.api
      .get()
      .url('/tasks/existing-active-task', args)
      .future();
  }

  async getMostRecentTask(args: {
    vin?: string;
    licensePlateNo?: string;
  }): Promise<VehicleInspectionTask | null> {
    return await this.api.get().url('/tasks/most-recent-task', args).future();
  }

  async list(
    filter: TaskListSearchParams,
    offset: number,
    limit: number,
    sortProperty?: 'createdAt' | 'finishedAt',
    sortDirection?: 'asc' | 'desc',
  ): Promise<TaskSearchResultItem[]> {
    return await this.api
      .get()
      .url('/tasks', {
        startDate: filter.startDate
          ? formatDateTime(filter.startDate, 'yyyy-MM-dd HH:mm:ss')
          : undefined,
        endDate: filter.endDate
          ? formatDateTime(filter.endDate, 'yyyy-MM-dd HH:mm:ss')
          : undefined,
        licensePlateNo: filter.licensePlateNo ?? undefined,
        keyword: filter.keyword ?? undefined,
        childStatus: filter.childStatus ?? undefined,
        status:
          filter.status != null && Array.isArray(filter.status)
            ? filter.status.join(',')
            : filter.status,
        customerType: filter.customerType,
        customerIsOnSite: filter.customerIsOnSite,
        carwash: filter.carwash,
        tag: filter.tag,
        serviceAgentId: filter.serviceAgentId,
        assignedTo: filter.assignedTo ?? undefined,
        brand: filter.brand,
        fuelType: filter.fuelType,
        ['mileage.start']: filter.mileageRange?.start,
        ['mileage.end']: filter.mileageRange?.end,
        ['mileage.startInclusive']:
          filter.mileageRange != null
            ? filter.mileageRange.startInclusive
            : undefined,
        ['mileage.endInclusive']:
          filter.mileageRange != null
            ? filter.mileageRange.endInclusive
            : undefined,
        parentTaskNo: filter.parentTaskNo,
        sortProperty,
        sortDirection,
        offset,
        limit,
      })
      .future();
  }

  async getWorkshopStats(
    startDate: Date,
    endDate: Date,
  ): Promise<StoreWorkshopStats> {
    return await this.api
      .get()
      .url('/tasks/workshop-stats', {
        startDate: formatDateTime(startDate, 'yyyy-MM-dd HH:mm:ss'),
        endDate: formatDateTime(endDate, 'yyyy-MM-dd HH:mm:ss'),
      })
      .future();
  }

  /** The construction jobs still open in the store, grouped by car. */
  async listWorkshopJobs(): Promise<StoreWorkshopJob[]> {
    return await this.api.get().url('/tasks/workshop-jobs').future();
  }

  async getTaskDetail(
    taskNo: string,
    options?: {
      projection?: TaskDetailProjection;
      projectionByVersions?: { [p in TaskDetailVersionKey]?: number };
    },
  ): Promise<VersionedResponse<TaskDetail>> {
    return await this.api
      .post()
      .url('/tasks/:taskNo/detail', {
        taskNo,
      })
      .data(options)
      .future();
  }

  async getTaskDetailVersions(taskNo: string): Promise<TaskDetailVersions> {
    return await this.api
      .get()
      .url('/tasks/:taskNo/detail/versions', {
        taskNo,
      })
      .future();
  }

  async getTaskExtraInfo(
    taskNo: string,
    options?: {
      issuesFilter?: VehicleIssuesFilter;
      serviceRecordsFilter?: VehicleServiceRecordsFilter;
    },
  ): Promise<TaskExtraInfo> {
    return await this.api
      .post()
      .url('/tasks/:taskNo/extra-info', {
        taskNo,
      })
      .data(options ?? {})
      .future();
  }

  async updateTaskInformation(
    taskNo: string,
    payload: UpdateTaskInformation,
  ): Promise<VersionedResponse<void>> {
    return await this.api
      .patch()
      .url('/tasks/:taskNo', { taskNo })
      .data({ payload })
      .future();
  }

  async getFinishTaskPreparedData(
    taskNo: string,
    withInsuranceExpiry?: boolean,
  ): Promise<FinishTaskPreparedData> {
    return await this.api
      .get()
      .url('/tasks/:taskNo/finish-task-prepared-data', {
        taskNo,
        with_insurance_expiry: withInsuranceExpiry,
      })
      .future();
  }

  async finishTask(
    taskNo: string,
    payload: FinishTaskRequestPayload,
  ): Promise<VersionedResponse<TaskBasicInfo>> {
    return await this.api
      .post()
      .url('/tasks/:taskNo/finish', { taskNo })
      .data(payload)
      .future();
  }

  //#endregion

  //#region obd diagnostics

  async listTaskTroubleCodes(
    taskNo: string,
  ): Promise<VersionedResponse<ObdTroubleCode[]>> {
    return await this.api
      .get()
      .url('/tasks/:taskNo/trouble-codes', { taskNo })
      .future();
  }

  async insertTaskTroubleCodes(
    taskNo: string,
    troubleCodes: ObdTroubleCodeCommitPayload[],
  ): Promise<VersionedResponse<ObdTroubleCode[]>> {
    return await this.api
      .post()
      .url('/tasks/:taskNo/trouble-codes', { taskNo })
      .data(troubleCodes)
      .future();
  }

  async removeTaskTroubleCodes(
    taskNo: string,
    codes: string[],
  ): Promise<VersionedResponse<void>> {
    return await this.api
      .delete()
      .url('/tasks/:taskNo/trouble-codes', { taskNo })
      .data(codes)
      .future();
  }
  //#endregion

  //#region inspection flows

  async getTaskInspectionFlows(
    category: InspectionCategory,
    taskNo: string,
  ): Promise<VersionedResponse<VehicleInspectionFlow[]>> {
    return await this.api
      .get()
      .url('/tasks/:taskNo/inspections/flows', { category, taskNo })
      .future();
  }

  async getTaskInspectionFlowInfo(
    category: InspectionCategory,
    taskNo: string,
    id: string,
  ): Promise<VehicleInspectionFlow> {
    return await this.api
      .get()
      .url('/tasks/:taskNo/inspections/flows/:id', { category, taskNo, id })
      .future();
  }

  /**
   * this method is idemponent, which means you can call this method with
   * the same parameters many times without errors.
   */
  async addTaskInspectionFlow(
    taskNo: string,
    params: {
      category: InspectionCategory;
      templateId: number;
      assignToMemberId: number;
    },
  ): Promise<VersionedResponse<VehicleInspectionFlow>> {
    return await this.api
      .post()
      .url('/tasks/:taskNo/inspections/flows', { taskNo })
      .data(params)
      .future();
  }

  async beginTaskInspectionFlow(
    taskNo: string,
    category: InspectionCategory,
    id: string,
  ): Promise<VersionedResponse<VehicleInspectionFlow>> {
    return await this.api
      .post()
      .url('/tasks/:taskNo/inspections/flows/:id/begin', { taskNo, id })
      .data({
        category,
      })
      .future();
  }

  async finishTaskInspectionFlow(
    category: InspectionCategory,
    taskNo: string,
    id: string,
    summary: InspectionFlowSummary,
    options?: {
      technicianId?: number;
      technicianName?: string;
      labels?: InspectedItemLabel[];
      remark?: string;
    },
  ): Promise<VersionedResponse<VehicleInspectionFlow>> {
    return await this.api
      .post()
      .url('/tasks/:taskNo/inspections/flows/:id/finish', { taskNo, id })
      .data({
        category,
        summary,
        ...options,
      })
      .future();
  }

  async requestAmendTaskInspectionFlow(
    taskNo: string,
    category: InspectionCategory,
    id: string,
  ): Promise<VersionedResponse<VehicleInspectionFlow>> {
    return await this.api
      .post()
      .url('/tasks/:taskNo/inspections/flows/:id/amend', { taskNo, id })
      .data({
        category,
      })
      .future();
  }

  async updateInspectionFlowAssignee(
    taskNo: string,
    category: InspectionCategory,
    id: string,
    assignee: number,
  ): Promise<VersionedResponse<VehicleInspectionFlow>> {
    return await this.api
      .put()
      .url('/tasks/:taskNo/inspections/flows/:id/assignee', { taskNo, id })
      .data({
        category,
        assignee,
      })
      .future();
  }

  async cancelTaskInspectionFlow(
    taskNo: string,
    category: InspectionCategory,
    id: string,
  ): Promise<VersionedResponse<TaskDetail>> {
    return await this.api
      .post()
      .url('/tasks/:taskNo/inspections/flows/:id/cancel', { taskNo, id })
      .data({
        category,
      })
      .future();
  }

  //#endregion

  //#region inspections

  async beginPreInspection(
    taskNo: string,
    technicianId?: number,
  ): Promise<VersionedResponse<VehicleInspectionFlow[]>> {
    return await this.api
      .post()
      .url('/tasks/:taskNo/inspections/pre-inspection/begin', { taskNo })
      .data({ technicianId })
      .future();
  }

  async getTaskInspectionDetail(
    taskNo: string,
    category: InspectionCategory,
    options: {
      types?: SiteInspectionType[];
      recursive?: boolean;
    },
  ): Promise<VersionedResponse<InspectionDetail>> {
    return await this.api
      .get()
      .url('/tasks/:taskNo/inspections/detail', {
        taskNo,
        category,
        types: options?.types?.join(','),
        recursive: options?.recursive,
      })
      .future();
  }

  async getSiteInspectionDetail(
    taskNo: string,
    siteId: number,
  ): Promise<VersionedResponse<SiteInspection | null>> {
    return await this.api
      .get()
      .url('/tasks/:taskNo/inspections/detail/sites/:siteId', {
        taskNo,
        siteId,
      })
      .future();
  }

  async commitSiteInspection(
    taskNo: string,
    payload: SiteInspectionCommitPayload,
    options?: {
      simulateError?: boolean;
    },
  ): Promise<VersionedResponse<SiteInspection>> {
    return await this.api
      .post()
      .url('/tasks/:taskNo/inspections/sites/:siteId/commits', {
        taskNo,
        siteId: payload.siteId,
        simulate_error: options?.simulateError,
      })
      .data({ payload })
      .future();
  }

  async batchCommitSiteInspections(
    taskNo: string,
    params: {
      inspectionType: SiteInspectionType;
      payloads: SiteInspectionCommitPayload[];
      technicianId?: number;
      technicianName?: string;
    },
  ): Promise<VersionedResponse<SiteInspection[]>> {
    return await this.api
      .post()
      .url('/tasks/:taskNo/inspections/sites/batch-commits', {
        taskNo,
      })
      .data(params)
      .future();
  }

  async saveCustomIssue(
    taskNo: string,
    payload: CustomIssueCommitPayload,
  ): Promise<VersionedResponse<CustomIssue>> {
    return await this.api
      .post()
      .url('/tasks/:taskNo/inspections/custom-issues', { taskNo })
      .data({ payload })
      .future();
  }

  async deleteCustomIssue(
    taskNo: string,
    issueId: number,
  ): Promise<VersionedResponse<void>> {
    return await this.api
      .delete()
      .url('/tasks/:taskNo/inspections/custom-issues/:issueId', {
        taskNo,
        issueId,
      })
      .future();
  }

  async cancelSiteInspection(
    taskNo: string,
    siteId: number,
  ): Promise<VersionedResponse<void>> {
    return await this.api
      .delete()
      .url('/tasks/:taskNo/inspections/sites/:siteId', { taskNo, siteId })
      .future();
  }

  async requestAmendInspectionResults(
    taskNo: string,
    category: InspectionCategory,
    options?: {
      technicianId?: number;
      technicianName?: string;
    },
  ): Promise<VersionedResponse<void>> {
    return await this.api
      .post()
      .url('/tasks/:taskNo/inspections/results/amends', {
        taskNo,
      })
      .data({ category, ...options })
      .future();
  }

  async finishInspections(
    taskNo: string,
    category: InspectionCategory,
    options?: {
      labels?: InspectedItemLabel[] | null;
      technicianId?: number;
      technicianName?: string;
      remark?: string;
    },
  ): Promise<VersionedResponse<void>> {
    return await this.api
      .post()
      .url('/tasks/:taskNo/inspections/finish', {
        taskNo,
      })
      .data({ category, ...options })
      .future();
  }

  async cancelInspections(
    taskNo: string,
    category: InspectionCategory,
    options?: {
      technicianId?: number;
      technicianName?: string;
    },
  ): Promise<VersionedResponse<void>> {
    return await this.api
      .delete()
      .url('/tasks/:taskNo/inspections', {
        taskNo,
      })
      .data({ category, ...options })
      .future();
  }

  async listObdTroubleCodes(
    taskNo: string,
  ): Promise<VersionedResponse<ObdTroubleCode[]>> {
    return await this.api
      .get()
      .url('/tasks/:taskNo/inspections/obd/trouble-codes', { taskNo })
      .future();
  }

  async saveObdTroubleCodes(
    taskNo: string,
    payloads: ObdTroubleCodeCommitPayload[],
  ): Promise<VersionedResponse<ObdTroubleCode[]>> {
    return await this.api
      .post()
      .url('/tasks/:taskNo/inspections/obd/trouble-codes', { taskNo })
      .data({ payloads })
      .future();
  }

  async removeObdTroubleCodes(
    taskNo: string,
    codes: string[],
  ): Promise<VersionedResponse<void>> {
    return await this.api
      .delete()
      .url('/tasks/:taskNo/inspections/obd/trouble-codes', { taskNo })
      .data({ codes })
      .future();
  }

  //#endregion

  //#region constructions

  async fetchConstructionWorkItems(
    taskNo: string,
  ): Promise<ConstructionWorkItemDetail[]> {
    return await this.api
      .get()
      .url('/tasks/:taskNo/constructions/work-items', { taskNo })
      .future();
  }

  async getConstructionDetail(
    taskNo: string,
  ): Promise<VersionedResponse<Construction>> {
    return await this.api
      .get()
      .url('/tasks/:taskNo/constructions/detail', { taskNo })
      .future();
  }

  async getConstructionJobMediasFromInspectionResults(
    taskNo: string,
    jobId: number,
  ): Promise<ConstructionJobMedia[]> {
    return await this.api
      .get()
      .url(
        '/tasks/:taskNo/constructions/jobs/:jobId/medias-from-inspection-results',
        { taskNo, jobId },
      )
      .future();
  }

  async addCustomConstructionJob(
    taskNo: string,
    params: {
      name: string;
      technicianId?: number;
      technicianName?: string;
      contextData?: ConstructionJobContextData;
      options?: {
        restrictFallbackUserRole?: boolean;
      };
    },
  ): Promise<VersionedResponse<ConstructionJob>> {
    return await this.api
      .post()
      .url('/tasks/:taskNo/constructions/custom-jobs', { taskNo })
      .data(params)
      .future();
  }

  async addConstructionJob(
    taskNo: string,
    params: {
      name: string;
      technicianId?: number;
      technicianName?: string;
      contextData?: ConstructionJobContextData;
      options?: {
        restrictFallbackUserRole?: boolean;
      };
    },
  ): Promise<VersionedResponse<ConstructionJob>> {
    return await this.api
      .post()
      .url('/tasks/:taskNo/constructions/jobs/add', { taskNo })
      .data(params)
      .future();
  }

  async updateConstructionJobName(
    taskNo: string,
    jobId: number,
    name: string,
  ): Promise<VersionedResponse<void>> {
    return await this.api
      .put()
      .url('/tasks/:taskNo/constructions/jobs/:jobId/name', { taskNo, jobId })
      .data({ name })
      .future();
  }

  async updateConstructionJobContextData(
    taskNo: string,
    jobId: number,
    contextData: ConstructionJobContextData,
  ): Promise<VersionedResponse<void>> {
    return await this.api
      .put()
      .url('/tasks/:taskNo/constructions/jobs/:jobId/context', {
        taskNo,
        jobId,
      })
      .data({ contextData })
      .future();
  }

  async confirmConstructionJobProductAdoption(
    taskNo: string,
    jobId: number,
    confirmationItem: ConstructionJobProductConfirmationItemPayload,
  ): Promise<VersionedResponse<ConstructionJobProductConfirmationResult>> {
    return await this.api
      .post()
      .url('/tasks/:taskNo/constructions/jobs/:jobId/products', {
        taskNo,
        jobId,
      })
      .data({ confirmationItem })
      .future();
  }

  async scheduleConstructionJobs(
    taskNo: string,
    payloads: ScheduleConstructionJobPayload[],
    technicianId?: number,
    technicianName?: string,
    options?: {
      restrictFallbackUserRole?: boolean;
    },
  ): Promise<VersionedResponse<ConstructionJob[]>> {
    return await this.api
      .post()
      .url('/tasks/:taskNo/constructions/jobs', { taskNo })
      .data({ payloads, technicianId, technicianName, options })
      .future();
  }

  async getConstructionJobDetail(
    taskNo: string,
    jobId: number,
  ): Promise<VersionedResponse<ConstructionJob>> {
    return await this.api
      .get()
      .url('/tasks/:taskNo/constructions/jobs/:jobId', { taskNo, jobId })
      .future();
  }

  async beginScheduledConstructionJob(
    taskNo: string,
    jobId: number,
    technicianId?: number,
    technicianName?: string,
  ): Promise<VersionedResponse<ConstructionJob>> {
    return await this.api
      .post()
      .url('/tasks/:taskNo/constructions/jobs/:jobId/begin', { taskNo, jobId })
      .data({ technicianId, technicianName })
      .future();
  }

  async deleteScheduledConstructionJob(
    taskNo: string,
    jobId: number,
    params?: {
      technicianId?: number;
      technicianName?: string;
      confirmedDeliveryCheckIdToBeDeleted?: number;
    },
  ): Promise<VersionedResponse<void>> {
    return await this.api
      .delete()
      .url('/tasks/:taskNo/constructions/jobs/:jobId', { taskNo, jobId })
      .data(params)
      .future();
  }

  async commitScheduledConstructionJob(
    taskNo: string,
    payload: ConstructionJobCommitPayload,
  ): Promise<VersionedResponse<ConstructionJob>> {
    return await this.api
      .post()
      .url('/tasks/:taskNo/constructions/jobs/:jobId/commits', {
        taskNo,
        jobId: payload.id,
      })
      .data({ payload })
      .future();
  }

  async patchScheduledConstructionJob(
    taskNo: string,
    payload: PatchConstructionJobPayload,
  ): Promise<VersionedResponse<ConstructionJob>> {
    return await this.api
      .patch()
      .url('/tasks/:taskNo/constructions/jobs/:jobId/commits', {
        taskNo,
        jobId: payload.id,
      })
      .data({ payload })
      .future();
  }

  async finishConstructionJobs(
    taskNo: string,
    remark?: string,
  ): Promise<VersionedResponse<void>> {
    return await this.api
      .post()
      .url('/tasks/:taskNo/constructions/jobs/finish', {
        taskNo,
      })
      .data({ remark })
      .future();
  }

  async requestUpdateConstructionJobs(
    taskNo: string,
  ): Promise<VersionedResponse<void>> {
    return await this.api
      .post()
      .url('/tasks/:taskNo/constructions/jobs/amends', {
        taskNo,
      })
      .future();
  }

  async cancelConstructions(taskNo: string): Promise<void> {
    await this.api
      .delete()
      .url('/tasks/:taskNo/constructions', {
        taskNo,
      })
      .future();
  }

  //#endregion

  //#region quotation
  async getQuotationForm(
    taskNo: string,
  ): Promise<VersionedResponse<QuotationForm | null>> {
    return await this.api
      .get()
      .url('/tasks/:taskNo/quotation/form', { taskNo })
      .future();
  }

  async saveQuotationForm(
    taskNo: string,
    payload: QuotationFormPayload,
    options?: { confirmed?: boolean },
  ): Promise<VersionedResponse<QuotationForm>> {
    return await this.api
      .post()
      .url('/tasks/:taskNo/quotation/form', { taskNo })
      .data({ payload, options })
      .future();
  }

  async getSettlementForm(
    taskNo: string,
  ): Promise<VersionedResponse<SettlementForm>> {
    return await this.api
      .get()
      .url('/tasks/:taskNo/quotation/settlement-form', { taskNo })
      .future();
  }

  async amendQuotationForm(
    taskNo: string,
    formId: IdType,
  ): Promise<VersionedResponse<QuotationForm>> {
    return await this.api
      .post()
      .url('/tasks/:taskNo/quotation/form/amend', { taskNo })
      .data({ formId })
      .future();
  }

  //#endregion

  //#region delivery checks

  async confirmPendingIssues(taskNo: string): Promise<VersionedResponse<void>> {
    return await this.api
      .post()
      .url('/tasks/:taskNo/delivery-checks/confirm-pending-issues', { taskNo })
      .future();
  }

  async getDeliveryCheckWorkItems(
    taskNo: string,
    sceneType: DeliveryCheckSceneType,
  ): Promise<DeliveryCheckWorkItem[]> {
    return await this.api
      .get()
      .url('/tasks/:taskNo/delivery-checks/work-items', { taskNo, sceneType })
      .future();
  }

  async getDeliveryChecks(
    taskNo: string,
  ): Promise<VersionedResponse<DeliveryCheck>> {
    return await this.api
      .get()
      .url('/tasks/:taskNo/delivery-checks/detail', { taskNo })
      .future();
  }

  async getSamplingChecks(
    taskNo: string,
  ): Promise<VersionedResponse<DeliveryCheck>> {
    return await this.api
      .get()
      .url('/tasks/:taskNo/delivery-checks/detail', { taskNo })
      .future();
  }

  async beginDeliveryChecks(
    taskNo: string,
    sceneType: DeliveryCheckSceneType,
  ): Promise<VersionedResponse<void>> {
    return await this.api
      .post()
      .url('/tasks/:taskNo/delivery-checks/begin', { taskNo })
      .data({ sceneType })
      .future();
  }

  async batchCommitDeliveryChecks(
    taskNo: string,
    payload: DeliveryCheckCommitPayload,
  ): Promise<VersionedResponse<DeliveryCheckItem[]>> {
    return await this.api
      .post()
      .url('/tasks/:taskNo/delivery-checks/batch-commits', { taskNo })
      .data({ payload })
      .future();
  }

  async saveDeliveryChecks(
    taskNo: string,
    payload: DeliveryCheckCommitPayload,
  ): Promise<VersionedResponse<DeliveryCheck>> {
    return await this.api
      .post()
      .url('/tasks/:taskNo/delivery-checks/save', { taskNo })
      .data({ payload })
      .future();
  }

  async saveSamplingChecks(
    taskNo: string,
    payload: DeliveryCheckCommitPayload,
  ): Promise<VersionedResponse<SamplingCheck>> {
    return await this.api
      .post()
      .url('/tasks/:taskNo/delivery-checks/save', { taskNo })
      .data({ payload })
      .future();
  }

  async finishDeliveryChecks(
    taskNo: string,
    payload: FinishDeliveryChecksPayload,
  ): Promise<VersionedResponse<void>> {
    return await this.api
      .post()
      .url('/tasks/:taskNo/delivery-checks/v2/finish', { taskNo })
      .data(payload)
      .future();
  }

  async completeDeliveryChecksAndFinishTask(
    taskNo: string,
    remark?: string,
    signatureImgUrl?: string,
  ): Promise<VersionedResponse<void>> {
    return await this.api
      .post()
      .url('/tasks/:taskNo/delivery-checks/finish', { taskNo })
      .data({ remark, signatureImgUrl })
      .future();
  }

  async requestUpdateDeliveryChecks(
    taskNo: string,
    sceneType: DeliveryCheckSceneType,
  ): Promise<VersionedResponse<void>> {
    return await this.api
      .post()
      .url('/tasks/:taskNo/delivery-checks/amends', { taskNo })
      .data({ sceneType })
      .future();
  }

  async deleteDeliveryCheck(
    taskNo: string,
    id: number,
  ): Promise<VersionedResponse<void>> {
    return await this.api
      .delete()
      .url('/tasks/:taskNo/delivery-checks/:id', { taskNo, id })
      .future();
  }

  async cancelDeliveryChecks(
    taskNo: string,
    sceneType: DeliveryCheckSceneType,
    remark?: string,
    force?: boolean,
  ): Promise<VersionedResponse<void>> {
    return await this.api
      .delete()
      .url('/tasks/:taskNo/delivery-checks', { taskNo })
      .data({ sceneType, remark, force })
      .future();
  }

  //#endregion

  //#region maintenance plans

  async saveMaintenancePlanDetail(
    taskNo: string,
    payload: SaveMaintenancePlanDetailPayload,
  ): Promise<VersionedResponse<MaintenancePlan>> {
    return await this.api
      .post()
      .url('/tasks/:taskNo/maintenance-service-plan-details', { taskNo })
      .data({ payload })
      .future();
  }

  async deleteMaintenancePlanDetail(
    taskNo: string,
    detailId: number,
  ): Promise<VersionedResponse<void>> {
    return await this.api
      .delete()
      .url('/tasks/:taskNo/maintenance-service-plan-details/:detailId', {
        taskNo,
        detailId,
      })
      .future();
  }

  //#endregion

  async getTaskTimeline(
    taskNo: string,
    limit?: number,
  ): Promise<VehicleInspectionTaskEvent[]> {
    return await this.api
      .get()
      .url('/tasks/:taskNo/timeline', { taskNo, limit })
      .future();
  }

  async getReportWithPendingData(
    taskNo: string,
    projection: VehicleReportProjection,
  ): Promise<VehicleReport> {
    return await this.api
      .get()
      .url('/tasks/:taskNo/report-with-pending-data', { taskNo, ...projection })
      .future();
  }

  async getWeixinPCOpenSDKTicket(): Promise<string> {
    return await this.api.get().url('/weixin/pc/open-sdk/ticket').future();
  }

  async getMiniprogramReportSharingParams(
    target: { type: 'task' | 'report'; id: string },
    params?: {
      tab?: ReportTabKey;
      shareType?: 'sa';
      envTag?: EnvTagType;
      openid?: string;
      flowId?: string;
    },
  ): Promise<{
    appid: string;
    pcOpenAppId: string;
    userName: string;
    pagePath: string;
  }> {
    return await this.api
      .get()
      .url('/weixin/miniprogram/sharing/report', {
        taskno: target.type === 'task' ? target.id : undefined,
        reportno: target.type === 'report' ? target.id : undefined,
        openid: params?.openid,
        tab: params?.tab,
        share_type: params?.shareType,
        env_tag: params?.envTag,
        flow_id: params?.flowId,
      })
      .future();
  }

  async getSubscribeBindQrcodeImageUrl(
    taskNo: string,
    options?: {
      logo?: boolean;
      size?: number;
      action?: SubscribeBindActionType;
      __fake__?: boolean;
    },
  ): Promise<string> {
    const commonSearchQuery = await getCommonRequestParametersAsSearchQuery();
    return this.api.url('tasks/:taskNo/subscribe-bind/qrcode.png', {
      taskNo,
      ...options,
      ...commonSearchQuery,
    });
  }

  async getPendingIssues<T extends PendingIssueInfoWithDetail>(
    taskNo: string,
  ): Promise<T[]> {
    return await this.api
      .get()
      .url('/tasks/:taskNo/pending-issues', { taskNo })
      .future();
  }

  async notifyServiceUpdated(
    taskNo: string,
    options?: {
      title?: string;
      detail?: string;
      remark?: string;
      reportTab?: ReportTabKey;
    },
  ): Promise<void> {
    await this.api
      .post()
      .url('/tasks/:taskNo/notifications/service-updated', { taskNo })
      .data(options ?? {})
      .future();
  }

  async searchMedias(
    taskNo: string,
    sources?: MediaSourceType[],
  ): Promise<TaskMediaWithSource[]> {
    return await this.api
      .get()
      .url('/tasks/:taskNo/medias/search', {
        taskNo,
        sources:
          sources != null && sources.length > 0
            ? sources?.join(',')
            : undefined,
      })
      .future();
  }

  async getInspectionScore(
    taskNo: string,
    options?: { refresh?: boolean },
  ): Promise<number> {
    return await this.api
      .get()
      .url('/tasks/:taskNo/inspections/score', {
        taskNo,
        ...options,
      })
      .cache(false)
      .future();
  }

  //#region product manifest
  async getTaskProductManifest(
    taskNo: string,
  ): Promise<VersionedResponse<TaskProductManifest>> {
    return await this.api
      .get()
      .url('/tasks/:taskNo/products/manifest', { taskNo })
      .future();
  }

  async saveProductManifestItems(
    taskNo: string,
    payload: SaveTaskProductManifestItemsPayload,
  ): Promise<VersionedResponse<TaskProductManifestItem[]>> {
    return await this.api
      .post()
      .url('/tasks/:taskNo/products/manifest', { taskNo })
      .data(payload)
      .future();
  }

  async deleteTaskProductManifestItems(
    taskNo: string,
    items: TaskProductManifestItemIdentifier[],
  ): Promise<TaskProductManifestItem[]> {
    return await this.api
      .post()
      .url('/tasks/:taskNo/products/delete', { taskNo })
      .data(items)
      .future();
  }

  async clearTaskProductManifestItems(
    taskNo: string,
  ): Promise<VersionedResponse<void>> {
    return await this.api
      .post()
      .url('/tasks/:taskNo/products/clear', { taskNo })
      .future();
  }

  // Change the closer on a product manifest item. Picker scope:
  // current-store staff (the gateway forwards to a foundation function
  // that further gates closer.type to ServiceAgent | Technicians).
  async changeProductManifestItemCloser(
    taskNo: string,
    manifestItemId: IdType,
    closer: ProductAuthorInfo,
  ): Promise<VersionedResponse<void>> {
    return await this.api
      .post()
      .url('/tasks/:taskNo/product-manifest-items/:manifestItemId/closer', {
        taskNo,
        manifestItemId,
      })
      .data({ closer })
      .future();
  }
  //#endregion
}
